import React from "react";
import {
  Box,
  Button,
  SpaceBetween,
  StatusIndicator,
  Steps,
  StepsProps,
} from "@amzn/awsui-components-react";
import {
  RedriveHistory,
  STATUS_INDICATOR,
  STATUS_LABELS,
} from "./investigationUtils";
import {
  DocumentRedrive,
  documentRedriveRunning,
  documentRedriveSummary,
  readyForDlq,
} from "./documentRedrive";
import { READY_TO_RESOLVE_LABEL } from "./readyToResolve";

/**
 * The happy path an investigation walks. EXECUTING_REDRIVE and MONITORING_REDRIVE are
 * both system-owned and collapse into the single "Redrive" step. BLOCKED is not a step:
 * it is an overlay on the stage the case was in when it was blocked (`blocker.blockedFrom`),
 * so that stage stays highlighted with a "Blocked" marker and unblocking returns to it.
 */
const REDRIVE_STEP = "REDRIVE";
const MAIN_PATH = [
  "INVESTIGATING",
  "FIX_IN_PROGRESS",
  "READY_TO_REDRIVE",
  REDRIVE_STEP,
  "RESOLVED",
] as const;

const SYSTEM_STATES = new Set(["EXECUTING_REDRIVE", "MONITORING_REDRIVE"]);

/**
 * With a order redrive the path gains two steps between "Ready to redrive" and the DLQ
 * redrive: the order redrive itself and "Ready to redrive (DLQ)" (owner checks the CBEs).
 */
const DOCUMENT_STEP = "DOCUMENT_REDRIVE";
const READY_DLQ_STEP = "READY_TO_REDRIVE_DLQ";
const DOCUMENT_PATH = [
  "INVESTIGATING",
  "FIX_IN_PROGRESS",
  "READY_TO_REDRIVE",
  DOCUMENT_STEP,
  READY_DLQ_STEP,
  REDRIVE_STEP,
  "RESOLVED",
] as const;

/** Where a case on the document-redrive path is. */
const documentPathIndex = (stage: string, doc: DocumentRedrive): number => {
  if (documentRedriveRunning(stage, doc))
    return DOCUMENT_PATH.indexOf(DOCUMENT_STEP);
  // Checking the CBEs and clearing the DLQ are separate steps: once confirmed, the DLQ
  // redrive is the current step.
  if (readyForDlq(stage, doc))
    return DOCUMENT_PATH.indexOf(
      doc?.stage === "CONFIRMED" ? REDRIVE_STEP : READY_DLQ_STEP
    );
  if (SYSTEM_STATES.has(stage)) return DOCUMENT_PATH.indexOf(REDRIVE_STEP);
  return (DOCUMENT_PATH as readonly string[]).indexOf(stage);
};

/** Position of a status on the main path, or -1 for off-path states (BLOCKED, unknown). */
export const pathIndex = (status: string): number => {
  if (SYSTEM_STATES.has(status)) return MAIN_PATH.indexOf(REDRIVE_STEP);
  return (MAIN_PATH as readonly string[]).indexOf(status);
};

/**
 * The stage a BLOCKED case is parked in. Older records predate `blockedFrom`; for those we
 * fall back to INVESTIGATING, the only stage a case can be blocked from without a fix.
 */
export const blockedStage = (blockedFrom?: string | null): string =>
  blockedFrom && pathIndex(blockedFrom) >= 0 ? blockedFrom : "INVESTIGATING";

/**
 * Verb-first label for a status transition button so it can't be mistaken for the
 * current state. Direction is derived from the main path. From Monitoring redrive the
 * labels name the conclusion the human is drawing about the redrive, since the state names
 * alone don't answer "the redrive didn't work — what do I pick?".
 */
export const transitionLabel = (
  from: string,
  to: string,
  blockedFrom?: string | null
): string => {
  if (to === "RESOLVED") return "Resolve…";
  if (to === "WITHDRAWN") return "Cancel investigation…";
  if (to === "BLOCKED") return "Mark blocked…";
  if (from === "BLOCKED" && to === blockedStage(blockedFrom)) {
    return `Unblock (back to ${STATUS_LABELS[to]})`;
  }
  if (from === "MONITORING_REDRIVE") {
    if (to === "READY_TO_REDRIVE") return "Redrive again";
    if (to === "INVESTIGATING") return "Redrive didn't work — investigate";
    if (to === "FIX_IN_PROGRESS")
      return "Redrive didn't work — fix in progress";
  }
  if (to === "READY_TO_REDRIVE") return "Mark ready to redrive";
  if (to === "INVESTIGATING" && (from === "RESOLVED" || from === "WITHDRAWN")) {
    return "Reopen investigation";
  }
  const fromIdx =
    from === "BLOCKED" ? pathIndex(blockedStage(blockedFrom)) : pathIndex(from);
  const toIdx = pathIndex(to);
  if (fromIdx >= 0 && toIdx >= 0 && toIdx < fromIdx) {
    return `Move back to ${STATUS_LABELS[to]}`;
  }
  return `Move to ${STATUS_LABELS[to]}`;
};

/**
 * Sort transitions so the natural next action comes first: unblock, then forward moves in
 * path order, then backward moves, then Blocked, Cancel and Resolve at the end. From
 * Monitoring redrive the redrive step is behind us, so Resolve leads, "Redrive again" follows,
 * then the "didn't work" conclusions (Investigating before Fix in progress), then Blocked.
 */
export const orderTransitions = (
  from: string,
  transitions: string[],
  blockedFrom?: string | null
): string[] => {
  const base = from === "BLOCKED" ? blockedStage(blockedFrom) : from;
  const baseIdx = pathIndex(base);
  const rank = (to: string): number => {
    if (from === "BLOCKED" && to === base) return 0;
    if (from === "MONITORING_REDRIVE") {
      if (to === "RESOLVED") return 1;
      if (to === "READY_TO_REDRIVE") return 2;
      if (to === "INVESTIGATING") return 3;
      if (to === "FIX_IN_PROGRESS") return 4;
    }
    if (to === "BLOCKED") return 300;
    if (to === "WITHDRAWN") return 350;
    if (to === "RESOLVED") return 400;
    const idx = pathIndex(to);
    return idx > baseIdx ? 100 + idx : 200 - idx;
  };
  return [...transitions].sort((a, b) => rank(a) - rank(b));
};

interface InvestigationStatusStepperProps {
  status: string;
  blockedFrom?: string | null;
  /**
   * Redrive history for a case that is back on the investigative side of the path. When set,
   * the Redrive step is drawn as visited (warning / error) rather than as an untouched circle,
   * so the stepper doesn't claim no redrive was ever tried.
   */
  redrive?: RedriveHistory | null;
  /** Extra content rendered under the current step (e.g. the Monitoring redrive watch). */
  currentDetails?: React.ReactNode;
  /** Vertical suits a narrow side column; horizontal (default) a full-width row. */
  orientation?: "horizontal" | "vertical";
  /**
   * The case has no owner. Its stored status is still INVESTIGATING, but nobody is, so the
   * first step reads "Needs an owner" with a warning icon until someone takes it.
   */
  unowned?: boolean;
  /** `investigation.documentRedrive`: draws the order redrive and "Ready to redrive (DLQ)" steps. */
  documentRedrive?: DocumentRedrive;
  /**
   * Monitoring passed (readyToResolve.monitoringClear): the Redrive step is drawn done as
   * "Monitoring passed" with this evidence line, and Resolved becomes the current step, read
   * as "Ready to resolve", carrying `currentDetails`.
   */
  monitoringClear?: string | null;
  /** Makes the current step's label a link (e.g. Monitoring redrive → the Redrives tab). */
  onCurrentStepClick?: () => void;
  /** Makes a past Redrive step ("Redrive (1 attempt)", "Monitoring passed") a link to its attempts. */
  onRedriveStepClick?: () => void;
}

/** Step label for a visited Redrive step: "Redrive (1 attempt)" / "Deletion (2 attempts)". */
export const visitedRedriveLabel = (redrive: RedriveHistory): string => {
  const noun = redrive.kind === "DELETE" ? "Deletion" : "Redrive";
  const attempts = `${redrive.attempts} attempt${
    redrive.attempts === 1 ? "" : "s"
  }`;
  return `${noun} (${attempts})`;
};

/**
 * Headline with the current status plus a stepper along the main path.
 * Completed steps are ticked, the current one carries its own status indicator and a
 * "Current" caption (or "Blocked" when parked), upcoming steps are hollow circles.
 */
export const InvestigationStatusStepper: React.FC<
  InvestigationStatusStepperProps
> = ({
  status,
  blockedFrom,
  redrive,
  currentDetails,
  orientation = "horizontal",
  unowned = false,
  documentRedrive,
  monitoringClear,
  onCurrentStepClick,
  onRedriveStepClick,
}) => {
  const isBlocked = status === "BLOCKED";
  // A withdrawn case never walked the path: it was let go, not resolved. Only the headline.
  const isWithdrawn = status === "WITHDRAWN";
  const stage = isBlocked ? blockedStage(blockedFrom) : status;
  const onDocumentPath = !!documentRedrive;
  const path: readonly string[] = onDocumentPath ? DOCUMENT_PATH : MAIN_PATH;
  const quiet =
    status === "MONITORING_REDRIVE" &&
    monitoringClear !== undefined &&
    monitoringClear !== null;
  const current = quiet
    ? path.indexOf("RESOLVED")
    : onDocumentPath
    ? documentPathIndex(stage, documentRedrive)
    : pathIndex(stage);

  // Plain Steps: completed steps are green ticks, the current one carries its own status
  // indicator and a bold label, upcoming ones are hollow grey circles. No captions — the
  // headline above already says where we are and why. The one exception is a Redrive step
  // that lies ahead but was already tried: it keeps its outcome (warning/error) visible.
  const steps: StepsProps.Step[] = path.map((step, idx) => {
    const isCurrent = idx === current;
    let label = STATUS_LABELS[step];
    let details: React.ReactNode = isCurrent ? currentDetails : undefined;
    if (quiet && step === REDRIVE_STEP) {
      label = "Monitoring passed";
      details = (
        <Box
          color="text-body-secondary"
          fontSize="body-s"
          data-testid="monitoring-clear-summary"
        >
          {monitoringClear}
        </Box>
      );
    } else if (step === REDRIVE_STEP) {
      label = isCurrent
        ? onDocumentPath && status === "READY_TO_REDRIVE"
          ? "DLQ redrive"
          : STATUS_LABELS[status]
        : onDocumentPath
        ? "DLQ redrive"
        : "Redrive";
    }
    // The step is where the owner checks the republished CBEs; name it for that (the long
    // status label wraps badly in a narrow step column).
    if (step === READY_DLQ_STEP)
      label =
        documentRedrive?.stage === "CONFIRMED"
          ? "CBEs confirmed"
          : "Check CBEs";
    if (step === DOCUMENT_STEP) {
      label = "Order redrive";
      const summary = documentRedriveSummary(documentRedrive);
      if (!isCurrent && summary) {
        details = (
          <Box color="text-body-secondary" fontSize="body-s">
            {summary}
          </Box>
        );
      }
    }
    const awaitingOwner = isCurrent && unowned && status === "INVESTIGATING";
    let stepStatus: StepsProps.Status = "not-started";
    let iconLabel = "Not started";
    if (quiet && isCurrent) {
      // Nothing left for Lusca to watch: the owner's next move is Resolve.
      label = READY_TO_RESOLVE_LABEL;
      stepStatus = "in-progress";
      iconLabel = "Current step (ready to resolve)";
    } else if (awaitingOwner) {
      label = "Needs an owner";
      stepStatus = "warning";
      iconLabel = "Current step (needs an owner)";
    } else if (isCurrent) {
      // Monitoring is Lusca checking on its own (hourly return detection): a spinner on the
      // step says "the system is working", where the pending clock read as "paused".
      // Blocked: the parked stage carries a red error icon.
      stepStatus = isBlocked
        ? "error"
        : status === "MONITORING_REDRIVE" || step === DOCUMENT_STEP
        ? "loading"
        : STATUS_INDICATOR[status] || "in-progress";
      iconLabel = isBlocked ? "Current step (blocked)" : "Current step";
    } else if (step === DOCUMENT_STEP && documentRedrive?.stage === "FAILED") {
      stepStatus = "error";
      iconLabel = "Order redrive failed";
    } else if (current >= 0 && idx < current) {
      stepStatus = "success";
      iconLabel = "Completed";
    } else if (step === REDRIVE_STEP && redrive) {
      stepStatus = redrive.outcome === "failed" ? "error" : "warning";
      iconLabel =
        redrive.outcome === "failed"
          ? "Previous attempt failed"
          : redrive.outcome === "returned"
          ? "Previous attempt had returns"
          : "Previously attempted";
      label = visitedRedriveLabel(redrive);
    }
    // A Redrive step that already ran (not the current one) links to its attempts.
    const pastRedrive =
      !isCurrent && step === REDRIVE_STEP && (quiet || !!redrive);
    return {
      status: stepStatus,
      statusIconAriaLabel: iconLabel,
      header: isCurrent ? (
        <Box fontWeight="bold" data-testid="current-step">
          {onCurrentStepClick ? (
            <Button variant="inline-link" onClick={onCurrentStepClick}>
              {label}
            </Button>
          ) : (
            label
          )}
        </Box>
      ) : pastRedrive && onRedriveStepClick ? (
        <Button
          variant="inline-link"
          onClick={onRedriveStepClick}
          data-testid="redrive-step-link"
        >
          {label}
        </Button>
      ) : (
        label
      ),
      details,
    };
  });

  // The stepper already shows where an on-path case is, and a blocked case's parked stage
  // carries a red error icon with the Blocked alert (reason) right below. Only spell the
  // status out when the stepper can't: Cancelled (no stepper) and any unknown/off-path status.
  const showHeadline = isWithdrawn || current < 0;

  return (
    <SpaceBetween size="s">
      {showHeadline && (
        <SpaceBetween direction="horizontal" size="xs" alignItems="center">
          <Box variant="awsui-key-label">Current status</Box>
          <Box fontSize="heading-s" data-testid="current-status">
            <StatusIndicator type={STATUS_INDICATOR[status] || "info"}>
              {STATUS_LABELS[status] || status}
              {isBlocked && ` during ${STATUS_LABELS[stage]}`}
            </StatusIndicator>
          </Box>
        </SpaceBetween>
      )}
      {!isWithdrawn && (
        <Steps
          orientation={orientation}
          ariaLabel="Investigation progress"
          steps={steps}
        />
      )}
    </SpaceBetween>
  );
};

export default InvestigationStatusStepper;
